<template>
  <div class="termination" :class="[
    { 'animate__animated animate__zoomOutLeft': isDeleting },
    { deleting: wasDeleted },
  ]">
    <div class="row box box--default">
      <div class="row__data row__data--medium row__data--title row__data--fixed">   
             
      </div>
      <div class="row__data row__data--medium">
        <div class="termination__data">{{ category }}</div>
      </div>
      <div class="row__data row__data--medium row__data--underline">
        <span @click="showSubmitDatePopup = true">
          <transition name="fade" mode="in-out">
            <PopupEditer v-if="showSubmitDatePopup" :isDate="true" @togglePopup="showSubmitDatePopup = $event"
              :selected="terminationDate" @submit="updateTerminationDate($event)" />
          </transition>
          {{ formatDateDisplay(terminationDate) }}
        </span>


        <!-- <div class="termination__data">{{ terminationDate }}</div> -->
      </div>




      <div class="row__data row__data--large">
        <div class="termination__data">{{ companyName }}</div>
      </div>
      <div class="row__data row__data--large">
        <EditableComment :comment="comment" :isRequired="true" :updateComment="updateTerminationComment" />
      </div>
      <div class="row__data row__data--medium row__data--half row__data--fixed">
        <div class="row__data row__data--small row__data--pdf icon icon__pdf"
          :class="{ 'icon__pdf--active': files.length }" @click="openPolicyFilesModal()"></div>
        <div class="row__data row__data--small icon icon__delete" @click="deleteTermination()"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from 'vue-i18n';
import { useRoute } from "vue-router";
import EditableComment from "@/partials/EditableComment";
import PopupEditer from "@/partials/PopupEditer"; 
// eslint-disable-next-line no-unused-vars
const { t } = useI18n();
defineOptions({
  name: "TerminationService"
});

const props = defineProps({
  service: {
    required: true,
    type: Object,
  },
  terminations: {
    required: true,
    type: Array,
  },
});

const store = useStore();
const route = useRoute();

// State
const isDeleting = ref(false);
const wasDeleted = ref(false);
const showSubmitDatePopup = ref(false);

// Computed
const category = computed(() => props.service.service.branch_category.code);
const terminationDate = computed(() => props.service.termination_date);
const companyName = computed(() => props.service.company.name);

const terminationEntry = computed(() =>
  props.terminations.find((t) => t.id === props.service.termination_id)
);

const customer = computed(() => terminationEntry.value?.customer_id);
const doubleCoverage = computed(() => terminationEntry.value?.double_coverage);
const comment = computed(() => terminationEntry.value?.comment);
const files = computed(() => terminationEntry.value?.termination_files);

const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const terminationTeamId = computed(() => store.getters.customer?.team_id ?? store.getters.customer?.team?.id ?? null);
const canEditTermination = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("policy_edit", terminationTeamId.value);
});

// Methods
const getGlobalCustomer = async () => {
  const { id } = route.params;
  await store.dispatch("getGlobalCustomer", id);
};

const deleteTermination = async () => {
  if (!canEditTermination.value) return;
  
  isDeleting.value = true;
  await store.dispatch("deleteTerminationService", props.service.id);
  wasDeleted.value = true;
  setTimeout(async () => {
    await getGlobalCustomer();
  }, 300);
};

const updateTerminationComment = async (comment) => {
  if (!canEditTermination.value) return;
  
  await store.dispatch("updateTerminationComment", {
    terminationId: props.service.termination_id,
    comment,
  });
  await getGlobalCustomer();
};

const openPolicyFilesModal = () => {
  if (!isSuperAdmin.value && !store.getters.hasTeamPermission("policy_create", terminationTeamId.value)) return;
  
  store.commit("setPolicyFileId", props.service.termination_id);
  store.commit("setPolicyFiles", files.value);
  store.commit("setPolicyFilesModalIsTermination", true);
  store.commit("setShowPolicyFilesModal", true);
};

const formatDate = (date) => {
  if (!date || typeof date !== 'string') return null;

  if (date.includes('-')) {
    const [year, month, day] = date.split('-');
    if (year && month && day && month.length === 2 && day.length === 2) {
      return `${year}-${month}-${day}`;
    }
  }

  const [day, month, year] = date.split('.');
  if (day && month && year && day.length === 2 && month.length === 2 && year.length === 4) {
    return `${year}-${month}-${day}`;
  }

  store.commit("setToast", { display: true, type: "error",   message: t("accountDetails.terminationsTable.messages.wrongDataFormat") });
  return null;
};

const formatDateDisplay = (date) => {
  if (date?.includes('-')) {
    const [year, month, day] = date.split('-');
    if (year && month && day && month.length === 2 && day.length === 2) {
      return `${day}.${month}.${year}`;
    }
  }
  return null;
};

const updateTerminationDate = async (submitDate) => {
  if (!canEditTermination.value) return;
  
  if (submitDate) {
    const formattedDate = formatDate(submitDate);

    const servicePayload = {
      id: props.service.id,
      company_id: props.service.company.id,
      service_id: props.service.service.id,
      termination_date: formattedDate
    };

    try {
      await store.dispatch("updateTermination", {
        terminationId: props.service.termination_id,
        customerId: customer.value,
        isDoubleCoverageId: doubleCoverage.value,
        text: comment.value,
        terminationServices: [servicePayload],
      });

      store.commit("setToast", { display: true, type: "success", message: t("accountDetails.terminationsTable.messages.terminationUpdatedSuccessfully") });
      showSubmitDatePopup.value = false;
      await getGlobalCustomer();
    } catch (err) {
      store.commit("setToast", { display: true, type: "error", message: t("accountDetails.terminationsTable.messages.somethingWentWrong") });
      showSubmitDatePopup.value = false;
    }
  } else {
    store.commit("setToast", { display: true, type: "error", message: t("accountDetails.terminationsTable.messages.selectADateFirst") });
    showSubmitDatePopup.value = false;
  }
};
</script>

<style lang="scss" scoped>
.termination {
  border-radius: var(--radius-1);
  transition: all 0.3s ease;
  max-height: 66px;
  margin-bottom: 15px;
  position: relative;

  .row {
    background: var(--bg-3);
    box-shadow: none;
    cursor: default;
    margin-bottom: 0;

    &__data {
      justify-content: flex-start;

      &--title {
        color: var(--text-12);
        font-size: var(--medium);
      }
    }

    &__data--half {
      flex-direction: row !important;
    }
  }

  &__data {
    color: var(--text-9);
  }
}

[data-theme="dark"] .termination {
  background: var(--bg-14);
}
</style>
